import { useEffect, useRef, useState } from 'react';

const DISCLAIMER_STORAGE_KEY = 'primetime_disclaimer_accepted';

function hasAcceptedDisclaimer() {
  try {
    return window.localStorage.getItem(DISCLAIMER_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function DisclaimerGate() {
  const [visible, setVisible] = useState(false);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const agreeButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    setVisible(!hasAcceptedDisclaimer());
  }, []);

  useEffect(() => {
    if (!visible) return;

    agreeButtonRef.current?.focus();

    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Tab' || !modalRef.current) return;

      const focusable = Array.from(
        modalRef.current.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
      ).filter((element) => !element.hasAttribute('disabled'));

      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [visible]);

  function acceptDisclaimer() {
    try {
      window.localStorage.setItem(DISCLAIMER_STORAGE_KEY, 'true');
    } catch {
      // If storage is unavailable, allow the current session to continue.
    }
    setVisible(false);
  }

  function leaveSite() {
    window.location.assign('https://www.google.com');
  }

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 px-4 py-6 backdrop-blur-md"
      role="presentation"
    >
      <section
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="disclaimer-title"
        className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-2xl border border-prime-red/45 bg-prime-black shadow-2xl"
      >
        <div className="border-b border-prime-red/30 bg-prime-red/10 px-5 py-3 sm:px-7">
          <p className="text-[10px] font-black uppercase tracking-[0.28em] text-prime-red">Member Access</p>
          <h2 id="disclaimer-title" className="mt-2 text-2xl font-black text-white sm:text-3xl">
            Professional DJ/VJ Use Disclaimer
          </h2>
        </div>

        <div className="space-y-3 px-5 py-5 text-[13px] leading-[1.55] text-white/75 sm:px-7">
          <p>
            PrimeTime DJ Pool is a professional DJ/VJ promotional pool intended to support the performance workflow of
            working DJs, VJs, label representatives, and approved music industry users.
          </p>
          <p>
            By clicking "I Agree," you certify that you are a working DJ, VJ, label representative, or approved music
            industry user. You acknowledge and agree that all tracks, edits, remixes, mashups, transitions, videos,
            previews, downloads, and other media accessed through PrimeTime DJ Pool are provided strictly for your own
            DJ/VJ performance, promotional, and professional use only.
          </p>
          <p>
            You further certify that you already own, have lawfully obtained, or have authorized access to the original
            music, video, or media works that may be contained within any remixes, edits, mashups, transitions, or
            related content accessed through PrimeTime DJ Pool.
          </p>
          <p>
            You agree that any content accessed through PrimeTime DJ Pool may not be sold, resold, reproduced, copied,
            re-uploaded, publicly posted, transferred, broadcast, shared, distributed, or redistributed in any form
            without proper authorization.
          </p>
          <p>
            You further acknowledge that you are solely responsible for complying with all applicable copyright laws,
            public-performance requirements, venue licensing rules, broadcaster restrictions, and any permissions
            required for the music, video, or media works you use in your performances.
          </p>
          <p>
            PrimeTime DJ Pool does not grant ownership of any music, video, edit, remix, mashup, transition, or media
            file. Access is provided solely for approved professional use within the scope described above.
          </p>
          <p>
            If you are not a working DJ, VJ, label representative, approved music industry user, or if you do not agree
            with these terms, do not enter or use this website.
          </p>
          <p className="font-bold text-white">
            By clicking "I Agree," you confirm that you have read, understood, and accepted the terms stated above.
          </p>
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-white/10 bg-black/35 px-5 py-2 sm:flex-row sm:justify-center sm:px-7">
          <button type="button" onClick={leaveSite} className="btn-secondary w-full sm:w-auto">
            Leave Site
          </button>
          <button ref={agreeButtonRef} type="button" onClick={acceptDisclaimer} className="btn-primary w-full sm:w-auto">
            I Agree
          </button>
        </div>
      </section>
    </div>
  );
}
